import { useEffect, useRef, useState } from 'react';

/**
 * useMoeVoice — browser MediaRecorder + SpeechSynthesis hooks for Moe.
 *
 * - start()/stop() capture webm/opus audio blobs (MediaRecorder default).
 * - speak(text) reads an answer in Moe's "radio voice" using SpeechSynthesis.
 * - Gracefully degrades if the browser lacks the APIs.
 */
export function useMoeVoice() {
  const [recording, setRecording] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [permission, setPermission] = useState(null); // null | 'granted' | 'denied'
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => () => stopStream(), []);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  const start = async () => {
    if (recording) return;
    if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      throw new Error('Microphone not available in this browser');
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setPermission('granted');
      chunksRef.current = [];
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.start();
      setRecording(true);
    } catch (e) {
      setPermission('denied');
      throw e;
    }
  };

  // Resolves to a Blob of the recorded audio. Call on press-release.
  const stop = () => new Promise((resolve) => {
    const mr = mediaRecorderRef.current;
    if (!mr || mr.state === 'inactive') {
      setRecording(false);
      resolve(null);
      return;
    }
    mr.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
      chunksRef.current = [];
      stopStream();
      setRecording(false);
      resolve(blob);
    };
    mr.stop();
  });

  const speak = (text, { voicePref = 'radio' } = {}) => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    // Pick a "radio" voice — deeper, steady. Fall back cleanly.
    const voices = window.speechSynthesis.getVoices();
    const preferred = [
      'Daniel', 'Alex', 'Fred', 'Google UK English Male',
      'Microsoft Guy Online', 'Microsoft David', 'Google US English',
    ];
    const chosen = preferred.map(n => voices.find(v => v.name.includes(n))).find(Boolean) || voices[0];
    if (chosen) u.voice = chosen;
    u.rate = 0.98;
    u.pitch = voicePref === 'radio' ? 0.9 : 1.0;
    u.volume = 1.0;
    u.onstart = () => setSpeaking(true);
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(u);
  };

  const shutUp = () => {
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setSpeaking(false);
  };

  return { recording, speaking, permission, start, stop, speak, shutUp };
}
